import React, { useState, useEffect, useRef, useMemo, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from "@material-ui/core";
import Loader from '../../../Loader.js';
import { LocalizationTheme } from '../../../../utils/utils.js';
import { GetUTCFormattedDate, DisplayMessage, TranslateDayOfWeekName } from '../../../../services/helpers.js';
import MaterialTable from 'material-table';
import Rodal from 'rodal';
import 'rodal/lib/rodal.css';
import moment from 'moment';
import 'moment/locale/nl';
import 'moment/locale/de';
import 'moment/locale/fr';
import Form from 'react-bootstrap/Form';
import { MuiThemeProvider } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import FontDownloadRoundedIcon from '@material-ui/icons/FontDownloadRounded';
import { sortTableDates, sortTableStringsNotCaseSensitive } from '../../../../utils/tableSorting.js';
import appFetch from '../../../../api/appFetch.js';
import { PlanningAbsencesWithDateFromAndDateTillUrl, PlanningWithIdUrl } from '../../../../constants/apiUrls.js';
import OutletsDropdown from '../../../Common/Dropdowns/OutletsDropdown.js';
import DatePickerComponent from '../../../Common/DatePickers/DatePickerComponent.js';
import { withPlanningSettings } from '../../../../utils/withPlanningSettings.js';

const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
    FontDownloadRoundedIcon: forwardRef((props, ref) => <FontDownloadRoundedIcon {...props} ref={ref} />)
};

const AbsencesTable = (props) => {
    const { resources, language, accessibleMoboOutlets, refreshGrid, onOutletChangeForSettings, canUseAgendasAndAbsencesInSettings } = props;

    const [outletId, setOutletId] = useState(() => accessibleMoboOutlets.length > 0 ? accessibleMoboOutlets[0].id : null);
    const [dataGrid, setDataGrid] = useState([]);
    const [fetchingData, setFetchingData] = useState(true);
    const [showDelete, setShowDelete] = useState(false);
    const [deletedItem, setDeletedItem] = useState(0);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [dateFrom, setDateFrom] = useState(() => moment().startOf('month').format('YYYY-MM-DD'));
    const [dateTill, setDateTill] = useState(() => moment().endOf('month').format('YYYY-MM-DD'));

    const outletIdRef = useRef(outletId);
    const dateFromRef = useRef(dateFrom);
    const dateTillRef = useRef(dateTill);
    useEffect(() => { outletIdRef.current = outletId; }, [outletId]);
    useEffect(() => { dateFromRef.current = dateFrom; }, [dateFrom]);
    useEffect(() => { dateTillRef.current = dateTill; }, [dateTill]);

    const canEdit = canUseAgendasAndAbsencesInSettings !== false;

    const getData = (outlet, absentFrom, absentTill) => {
        if (!outlet) return;
        if (!(absentFrom != "" && absentTill != "")) return;

        setFetchingData(true);
        let initialData = {};

        initialData.columns = [
            {
                title: resources["OutletUser"],
                width: "65%",
                field: 'assignedToName',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableStringsNotCaseSensitive(a.assignedToName, b.assignedToName)
            },
            {
                title: resources["AbsenceFrom"],
                field: 'absenceFrom',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableDates(a.absenceFrom, b.absenceFrom, "DD/MM/YYYY HH:mm:ss")
            },
            {
                title: resources["AbsenceTill"],
                field: 'absenceTill',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableDates(a.absenceTill, b.absenceTill, "HH:mm")
            },
            {
                title: resources["Reason"],
                field: 'reason',
                options: { filter: true, sort: true },
                customSort: (a, b) => sortTableStringsNotCaseSensitive(a.reason, b.reason)
            },
        ];

        let url = PlanningAbsencesWithDateFromAndDateTillUrl(absentFrom, absentTill);
        if (outlet != null) {
            url = url + `&moboOutletId=${outlet}`;
        }

        appFetch({
            url: url,
            onSuccess: (data) => {
                const currentRows = [];

                data.sort((el1, el2) => el1.id - el2.id).forEach(function (rowData) {
                    moment.locale("en");
                    const absenceDateDay = TranslateDayOfWeekName(moment(rowData.absentFrom).format('dddd'), resources);
                    moment.locale(language);
                    const localAbsenceFrom = GetUTCFormattedDate(rowData.absentFrom, false);
                    const localAbsenceTill = GetUTCFormattedDate(rowData.absentTill, true);

                    currentRows.push({
                        id: rowData.id,
                        assignedToName: `${rowData.assignedToName}`,
                        absenceFrom: `${absenceDateDay} ${moment(localAbsenceFrom).format('DD/MM/YYYY HH:mm').toLocaleString()}`,
                        absenceTill: localAbsenceTill,
                        reason: rowData.reason,
                    });
                });

                initialData.data = currentRows;

                setDataGrid(initialData);
            },
            onError: (data) => {
                for (let i = 0; i < data.errors.length; i++) {
                    DisplayMessage(data.errors[i], "error");
                }
                setDataGrid([]);
            },
            onFinalize: () => {
                setFetchingData(false);
            }
        })
    };

    useEffect(() => {
        if (onOutletChangeForSettings && outletIdRef.current) {
            onOutletChangeForSettings(outletIdRef.current);
        }
        getData(outletIdRef.current, dateFromRef.current, dateTillRef.current);
    }, []);

    useEffect(() => {
        if (refreshGrid) {
            getData(outletIdRef.current, dateFromRef.current, dateTillRef.current);
        }
    }, [refreshGrid]);

    const handleChangeOutlet = (e) => {
        const id = e.target.value;
        setOutletId(id);
        getData(id, dateFrom, dateTill);
        if (onOutletChangeForSettings) {
            onOutletChangeForSettings(id);
        }
    };

    const handleDateFrom = (value) => {
        if (value > dateTill) {
            setDateTill("");
        }
        setDateFrom(value);

        if (value < '2000-01-01') {
            setRequestInProgress(true);
        } else {
            setRequestInProgress(false);
            getData(outletId, value, dateTill);
        }
    };

    const handleDateTill = (value) => {
        setDateTill(value);

        if (value < '2000-01-01') {
            setRequestInProgress(true);
        } else {
            setRequestInProgress(false);
            getData(outletId, dateFrom, value);
        }
    };

    const handleCloseDelete = () => {
        setShowDelete(false);
        setDeletedItem(undefined);
    };

    const handleDeleteAbsence = () => {
        setRequestInProgress(true);
        const url = PlanningWithIdUrl(deletedItem);

        appFetch({
            url: url,
            method: 'DELETE',
            onSuccess: () => {
                DisplayMessage(resources["AbsenceWasDeleted"], "success");
                setShowDelete(false);
                setDeletedItem(0);
                setRequestInProgress(false);
                getData(outletIdRef.current, dateFromRef.current, dateTillRef.current);
            },
            onError: (data) => {
                for (let i = 0; i < data.errors.length; i++) {
                    DisplayMessage(data.errors[i], "error");
                }
            },
            onFinalize: () => {
                setShowDelete(false);
                setDeletedItem(0);
                setRequestInProgress(false);
            }
        })
    };

    const tableActions = useMemo(() => [
        {
            icon: () => <DeleteOutline style={{ opacity: canEdit ? 1 : 0.5 }} />,
            tooltip: resources["Delete"],
            disabled: !canEdit,
            onClick: (event, rowData) => {
                if (!canEdit) return;
                setShowDelete(true);
                setDeletedItem(rowData.id);
            }
        }
    ], [canEdit, resources]);

    return (
        <div>
            <div className="position-relative py-4">
                <h3>{resources["Absences"]}</h3>
                <div className={"centered-loader " + (fetchingData ? 'fadeIn' : 'fadeOut')}><CircularProgress /></div>
                <div className={fetchingData ? 'fadeOut' : 'fadeIn'}>
                    <Box className='mt-3' display="flex" justifyContent="space-between">
                        <Box display="flex" justifyContent="flex-start" style={{ gap: "15px" }}>
                            <Form.Group className="main-dropdown">
                                <OutletsDropdown
                                    resources={resources}
                                    outlets={accessibleMoboOutlets}
                                    filterFunction={x => x.hasWorkshopPlanning || x.hasWorkshopPlanningLight}
                                    outlet={outletId}
                                    onOutletChange={handleChangeOutlet}
                                    id="outlet-dropdown-grid"
                                    containerClass="col-md-12"
                                    noPlaceholder
                                />
                            </Form.Group>
                            <DatePickerComponent
                                id="dateFrom"
                                label={resources["AbsenceDateFrom"]}
                                value={dateFrom}
                                onChange={handleDateFrom}
                                error={!dateFrom}
                                minDate={new Date("2000-01-01")}
                                required
                            />
                            <DatePickerComponent
                                id="dateTill"
                                label={resources["AbsenceDateTill"]}
                                value={dateTill}
                                onChange={handleDateTill}
                                error={!dateTill}
                                minDate={dateFrom != "" ? new Date(dateFrom) : new Date("2000-01-01")}
                                required
                            />
                        </Box>
                        <div className="float-right">
                            <div className="float-right mb-3" >
                                <Button id="addNew" component={Link}
                                    to={{ pathname: "/absences/new", state: { outletId: outletId } }} color="primary" variant="contained" disabled={!canEdit}>
                                    <i className="fas fa-plus mr-2" title={resources["AddNewAbsence"]} />
                                    {resources["AddNewAbsence"]}
                                </Button>
                            </div>
                        </div>
                    </Box>
                    {!canEdit && (
                        <Typography variant="body2" color="error" style={{ marginBottom: 0, marginTop: '10px' }}>
                            {resources["UseShiftsFunctionalityToManageAgendasOrAbsences"]}
                        </Typography>
                    )}

                    <div style={{ clear: "both", }}></div>

                    <Paper elevation={3} className="p-3 mt-3">
                        <MuiThemeProvider theme={LocalizationTheme()}>
                            <MaterialTable
                                icons={tableIcons}
                                title={""}
                                localization={{
                                    header: { actions: resources["Actions"] },
                                    body: {
                                        emptyDataSourceMessage: resources["NoRecordsToBeDisplayed"],
                                        deleteTooltip: resources["DeleteTooltip"],
                                    },
                                    toolbar: {
                                        searchPlaceholder: resources["Search"],
                                        searchTooltip: resources["Search"]
                                    },
                                    pagination: {
                                        firstTooltip: resources["FirstTooltip"],
                                        lastTooltip: resources["LastTooltip"],

                                        previousTooltip: resources["PreviousTooltip"],
                                        nextTooltip: resources["NextTooltip"],

                                        labelRowsPerPage: resources["LabelRowsPerPage"],
                                        labelRowsSelect: resources["LabelRowsSelect"],
                                    }
                                }}
                                data={dataGrid.data}
                                columns={dataGrid.columns}
                                actions={tableActions}
                                options={{ emptyRowsWhenPaging: false, pageSize: 10, pageSizeOptions: [10, 15, 20] }}
                            />
                        </MuiThemeProvider>
                    </Paper>
                </div>
                <div className={"loader-overlap position-absolute " + (fetchingData ? 'fadeIn' : 'fadeOut')}><div className="container"><Loader /></div></div>

                <Rodal visible={showDelete} onClose={handleCloseDelete} className="rodal" customStyles={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'
                }}>
                    <div className="modal-body" style={{ marginTop: "20px", display: "flex", alignItems: "center" }}>{resources["AreYouSureYouWantToDeleteAbsence"]}</div>
                    <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <button id="btnCancelDelete" className="btn btn-secondary" style={{ backgroundColor: "white", color: "#53B3EA", borderColor: "#53B3EA" }} onClick={handleCloseDelete}
                            disabled={requestInProgress}>{resources["Cancel"]}</button>
                        <button className="btn btn-primary" id="btnDelete" style={{ backgroundColor: "#53B3EA", color: "white", borderColor: "#53B3EA" }} onClick={handleDeleteAbsence}
                            disabled={requestInProgress}>{resources["Yes"]}</button>
                    </div>
                    <span className="rodal-close"></span>
                </Rodal>
            </div>
        </div >
    );
};

export default withPlanningSettings(AbsencesTable);
